/**
 * Salary Distribution Chart Component
 * Shows multiple salary ranges ordered by specificity (most to least specific)
 */

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Info } from '@phosphor-icons/react';
import { isValidNumber, isPositiveNumber } from '../utils/numberUtils';
import { salaryDistributionColors } from '../utils/job-detail-chart-theme';
import { ChartPopoverPortal } from './ChartPopoverPortal';
import { usePrefersFineHover } from '../utils/usePrefersFineHover';
import { buildSalaryContextAnalysis } from '../../../../../utils/insights/buildSalaryContextAnalysis';
import { buildSalaryAnalysisNarrative } from '../../../../../utils/insights/buildSalaryAnalysisNarrative';
import type { JobSalaryListingProvenance } from '../../../../../utils/job-detail/resolve-job-salary-listing-provenance';
import { JD_MORE_INFO_CHART_HIT } from '../../../../../utils/job-detail/job-detail-more-info-affordance';
import { buildSalaryComparisonSeoRows } from '../utils/build-salary-comparison-seo-rows';

interface SalaryStatistics {
  median: { min: number; max: number };
  average: { min: number; max: number };
  min: number;
  max: number;
  percentile25: { min: number; max: number };
  percentile75: { min: number; max: number };
  percentile90?: { min: number; max: number };
  sampleSize: number;
  aggregationType?: string;
  dimensionValue?: string;
}

type SalaryJobRowKind = 'employer_quote' | 'extracted_no_quote' | 'benchmark_estimate';

interface SalaryRange {
  stats: SalaryStatistics;
  label: string;
  color: string;
  type: 'job' | 'titleLocation' | 'companyLocation' | 'titleRoleLevel' | 'title' | 'roleLevel' | 'locationMarket';
  /** Set when type === 'job': distinguishes listing pay vs modelled benchmark row. */
  jobRowKind?: SalaryJobRowKind;
}

interface SalaryDistributionChartProps {
  salaryStats?: SalaryStatistics;
  locationSalaryStats?: Record<string, SalaryStatistics>;
  allSalaryStats?: {
    titleLocation?: Record<string, SalaryStatistics>;
    companyTitleLocation?: Record<string, SalaryStatistics>;
    title?: SalaryStatistics;
    companyTitle?: SalaryStatistics;
    locations?: Record<string, SalaryStatistics>;
    roleLevel?: SalaryStatistics;
    companyLocation?: Record<string, SalaryStatistics>;
    titleRoleLevel?: SalaryStatistics;
  };
  jobSalaryMin?: number;
  jobSalaryMax?: number;
  currency?: string;
  salaryPeriod?: 'annual' | 'monthly' | 'daily' | 'hourly';
  locale?: string;
  className?: string;
  jobLocation?: string[];
  jobTitle?: string;
  jobRoleLevel?: string;
  /** How numeric job pay (if any) relates to text in the listing; drives row labels and popover copy. */
  jobSalaryListingProvenance?: JobSalaryListingProvenance;
}

export const SalaryDistributionChart: React.FC<SalaryDistributionChartProps> = memo(({
  salaryStats,
  locationSalaryStats,
  allSalaryStats,
  jobSalaryMin,
  jobSalaryMax,
  currency = '€',
  salaryPeriod = 'annual',
  locale = 'en',
  className = '',
  jobLocation = [],
  jobTitle,
  jobRoleLevel,
  jobSalaryListingProvenance = 'none',
}) => {
  const intl = useIntl();
  const prefersFineHover = usePrefersFineHover();
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverCloseTimer = useCallback(() => {
    if (hoverCloseTimer.current !== null) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  }, []);

  const scheduleCloseHover = useCallback(() => {
    clearHoverCloseTimer();
    hoverCloseTimer.current = setTimeout(() => {
      setActiveRowIndex(null);
      hoverCloseTimer.current = null;
    }, 220);
  }, [clearHoverCloseTimer]);

  useEffect(() => () => clearHoverCloseTimer(), [clearHoverCloseTimer]);

  const openRow = useCallback(
    (index: number) => {
      clearHoverCloseTimer();
      setActiveRowIndex(index);
    },
    [clearHoverCloseTimer],
  );

  const closePopover = useCallback(() => {
    clearHoverCloseTimer();
    setActiveRowIndex(null);
  }, [clearHoverCloseTimer]);

  const toggleRowClick = useCallback(
    (index: number) => {
      setActiveRowIndex((prev) => (prev === index ? null : index));
    },
    [],
  );

  const onRowPointerEnter = useCallback(
    (index: number) => {
      if (!prefersFineHover) return;
      openRow(index);
    },
    [prefersFineHover, openRow],
  );

  const onRowPointerLeave = useCallback(() => {
    if (!prefersFineHover) return;
    scheduleCloseHover();
  }, [prefersFineHover, scheduleCloseHover]);

  // Always use EUR, regardless of input currency
  const displayCurrency = '€';

  // Helper to get sample size
  const getValidSampleSize = (stats: SalaryStatistics | undefined | null): number => {
    if (!stats) return 0;
    const size = stats.sampleSize;
    return isValidNumber(size) && size >= 0 ? size : 0;
  };

  // Helper to find stats in a record by location
  const findStatsInRecord = (record: Record<string, SalaryStatistics> | undefined, locations: string[]): { stats: SalaryStatistics; location: string } | null => {
    if (!record) return null;
    if (Object.keys(record).length === 0) return null;
    for (const location of locations) {
      const stats = record[location];
      if (stats && getValidSampleSize(stats) > 0) {
        return { stats, location };
      }
    }
    return null;
  };

  // Normalize to monthly for consistent comparison
  const normalizeToMonthly = (value: number, period: string): number => {
    if (!isValidNumber(value) || value <= 0) return 0;
    switch (period) {
      case 'annual': return value / 12;
      case 'monthly': return value;
      case 'daily': return value * 22;
      case 'hourly': return value * 22 * 8;
      default: return value;
    }
  };

  // Collect all available salary ranges in order of specificity (least to most specific)
  // We'll reverse this when rendering so most specific is at the bottom
  const collectSalaryRanges = (): SalaryRange[] => {
    const ranges: SalaryRange[] = [];

    // 1. Title only (least specific) - goes at top
    if (allSalaryStats?.title && getValidSampleSize(allSalaryStats.title) > 0) {
      ranges.push({
        stats: allSalaryStats.title,
        label: intl.formatMessage(
          { id: 'salaryChart.marketAverage', defaultMessage: 'Market Average: {title}', description: 'Chart label for market average salary by job title, where {title} is the job title' },
          { title: jobTitle || intl.formatMessage({ id: 'salaryChart.thisRole', defaultMessage: 'this role', description: 'Fallback text when job title is not available, used in salary chart labels' }) }
        ),
        color: salaryDistributionColors.title,
        type: 'title',
      });
    }

    // 2. Title + Role Level (titleRoleLevel)
    if (allSalaryStats?.titleRoleLevel && getValidSampleSize(allSalaryStats.titleRoleLevel) > 0) {
      ranges.push({
        stats: allSalaryStats.titleRoleLevel,
        label: intl.formatMessage(
          { id: 'salaryChart.marketAverageRoleLevel', defaultMessage: 'Market Average: {roleLevel} Level', description: 'Chart label for market average salary by role level, where {roleLevel} is the level (e.g., Junior, Senior)' },
          { roleLevel: jobRoleLevel || intl.formatMessage({ id: 'salaryChart.thisRoleLevel', defaultMessage: 'this role level', description: 'Fallback text when role level is not available, used in salary chart labels' }) }
        ),
        color: salaryDistributionColors.titleRoleLevel,
        type: 'titleRoleLevel',
      });
    }

    // 2b. All roles in location (LOCATION-only aggregate)
    if (allSalaryStats?.locations && jobLocation.length > 0) {
      const found = findStatsInRecord(allSalaryStats.locations, jobLocation);
      if (found && getValidSampleSize(found.stats) > 0) {
        ranges.push({
          stats: found.stats,
          label: intl.formatMessage(
            {
              id: 'salaryChart.allRolesInLocation',
              defaultMessage: 'All roles in {location}',
              description: 'Chart label for salary stats aggregated across all job titles in a location',
            },
            { location: found.location },
          ),
          color: salaryDistributionColors.locationMarket,
          type: 'locationMarket',
        });
      }
    }

    // 3. Company + Location (companyLocation)
    if (allSalaryStats?.companyLocation && jobLocation.length > 0) {
      const found = findStatsInRecord(allSalaryStats.companyLocation, jobLocation);
      if (found && getValidSampleSize(found.stats) > 0) {
        ranges.push({
          stats: found.stats,
          label: intl.formatMessage(
            { id: 'salaryChart.companyLocation', defaultMessage: 'Company in {location}', description: 'Chart label for company salary data in specific location, where {location} is the city/region' },
            { location: found.location }
          ),
          color: salaryDistributionColors.companyLocation,
          type: 'companyLocation',
        });
      }
    }

    // 4. Title + Location (titleLocation)
    if (allSalaryStats?.titleLocation && jobLocation.length > 0) {
      const found = findStatsInRecord(allSalaryStats.titleLocation, jobLocation);
      if (found && getValidSampleSize(found.stats) > 0) {
        ranges.push({
          stats: found.stats,
          label: intl.formatMessage(
            { id: 'salaryChart.locationAverage', defaultMessage: 'In {location}: {title}', description: 'Chart label for average salary by location and title, where {location} is the city/region and {title} is the job title' },
            { 
              location: found.location,
              title: jobTitle || intl.formatMessage({ id: 'salaryChart.thisRole', defaultMessage: 'this role', description: 'Fallback text when job title is not available, used in salary chart labels' }) 
            }
          ),
          color: salaryDistributionColors.titleLocation,
          type: 'titleLocation',
        });
      }
    }

    // 5. Job salary (most specific) - or estimate if not provided - goes at bottom
    const hasJobSalary = isPositiveNumber(jobSalaryMin) && isPositiveNumber(jobSalaryMax);
    if (hasJobSalary) {
      const jobMinMonthly = normalizeToMonthly(jobSalaryMin!, salaryPeriod);
      const jobMaxMonthly = normalizeToMonthly(jobSalaryMax!, salaryPeriod);
      
      // Create a synthetic stats object for the job range
      const jobMid = (jobMinMonthly + jobMaxMonthly) / 2;
      const jobStats: SalaryStatistics = {
        median: { min: jobMid, max: jobMid },
        average: { min: jobMinMonthly, max: jobMaxMonthly },
        min: jobMinMonthly,
        max: jobMaxMonthly,
        percentile25: { min: jobMinMonthly, max: jobMid },
        percentile75: { min: jobMid, max: jobMaxMonthly },
        sampleSize: 1,
      };
      
      const fromListing = jobSalaryListingProvenance === 'employer_quote';
      const unverifiedExtract = jobSalaryListingProvenance !== 'employer_quote';

      const offerLabel =
        unverifiedExtract
          ? jobRoleLevel
            ? intl.formatMessage(
                {
                  id: 'salaryChart.payInDataNotInAdWithLevel',
                  defaultMessage: 'Pay in our data — not quoted in ad ({roleLevel})',
                  description: 'Chart row when listing has numeric pay without salaryDetails quote; {roleLevel} is seniority',
                },
                { roleLevel: jobRoleLevel },
              )
            : intl.formatMessage({
                id: 'salaryChart.payInDataNotInAd',
                defaultMessage: 'Pay in our data — not quoted in ad',
                description: 'Chart row when listing has numeric pay without salaryDetails quote',
              })
          : jobRoleLevel
            ? intl.formatMessage(
                {
                  id: 'salaryChart.fromJobAdWithLevel',
                  defaultMessage: 'From job ad ({roleLevel})',
                  description: 'Chart row for employer-stated pay with role level',
                },
                { roleLevel: jobRoleLevel },
              )
            : intl.formatMessage({
                id: 'salaryChart.fromJobAd',
                defaultMessage: 'From job ad',
                description: 'Chart row for employer-stated pay',
              });

      const jobRowKind: SalaryJobRowKind = unverifiedExtract ? 'extracted_no_quote' : 'employer_quote';

      ranges.push({
        stats: jobStats,
        label: offerLabel,
        color: salaryDistributionColors.job,
        type: 'job',
        jobRowKind,
      });
    } else if (ranges.length > 0) {
      // If no job salary, add an estimate at the end
      // Use the most specific range available to estimate
      const mostSpecific = ranges[ranges.length - 1];
      const p25Min = mostSpecific.stats.percentile25?.min || mostSpecific.stats.median.min * 0.85;
      const p75Max = mostSpecific.stats.percentile75?.max || mostSpecific.stats.median.max * 1.15;
      const estimatedMid = (p25Min + p75Max) / 2;
      
      const estimatedStats: SalaryStatistics = {
        median: { min: estimatedMid, max: estimatedMid },
        average: { min: p25Min, max: p75Max },
        min: p25Min,
        max: p75Max,
        percentile25: { min: p25Min, max: estimatedMid },
        percentile75: { min: estimatedMid, max: p75Max },
        sampleSize: mostSpecific.stats.sampleSize,
      };
      
      ranges.push({
        stats: estimatedStats,
        label: intl.formatMessage({
          id: 'salaryChart.estimatedFromMarket',
          defaultMessage: 'Estimated from market data (no pay in ad)',
          description: 'Chart label for modelled pay when the job ad has no salary numbers',
        }),
        color: salaryDistributionColors.job,
        type: 'job',
        jobRowKind: 'benchmark_estimate',
      });
    }

    return ranges;
  };

  const salaryRanges = collectSalaryRanges();


  // If no ranges, don't render
  if (salaryRanges.length === 0) {
    return null;
  }

  // Calculate overall chart range from all ranges (include 0 so scale matches bar positions)
  let chartMin = Infinity;
  let chartMax = -Infinity;

  for (const r of salaryRanges) {
    if (isValidNumber(r.stats.min) && r.stats.min < chartMin) {
      chartMin = r.stats.min;
    }
    if (isValidNumber(r.stats.max) && r.stats.max > chartMax) {
      chartMax = r.stats.max;
    }
  }

  if (chartMin === Infinity || chartMax === -Infinity || chartMax <= chartMin) {
    return null;
  }

  // Format currency - always use EUR
  const formatCurrency = (value: number): string => {
    if (!isValidNumber(value) || value <= 0) return displayCurrency + '0';
    const rounded = Math.round(value);
    return `${displayCurrency}${rounded.toLocaleString(locale)}`;
  };

  const formatPeriod = (period: string): string => {
    switch (period) {
      case 'annual': return intl.formatMessage({ id: 'salaryStats.perYear', defaultMessage: 'per year' });
      case 'monthly': return intl.formatMessage({ id: 'salaryStats.perMonth', defaultMessage: 'per month' });
      case 'daily': return intl.formatMessage({ id: 'salaryStats.perDay', defaultMessage: 'per day' });
      case 'hourly': return intl.formatMessage({ id: 'salaryStats.perHour', defaultMessage: 'per hour' });
      default: return '';
    }
  };

  // Calculate median midpoint
  const getMedianMid = (stats: SalaryStatistics): number => {
    const medianMin = stats.median?.min || 0;
    const medianMax = stats.median?.max || 0;
    if (medianMin > 0 && medianMax > 0) {
      return (medianMin + medianMax) / 2;
    } else if (medianMin > 0) {
      return medianMin;
    } else if (medianMax > 0) {
      return medianMax;
    }
    return (stats.min + stats.max) / 2;
  };

  const formatSpread = (lo: number, hi: number): string => {
    if (!isValidNumber(lo) || !isValidNumber(hi)) return '—';
    if (lo <= 0 && hi <= 0) return '—';
    if (Math.abs(lo - hi) < 0.5) return formatCurrency(lo);
    return `${formatCurrency(lo)} – ${formatCurrency(hi)}`;
  };

  const formatDimensionValue = (raw: string): string => {
    if (!raw) return '';
    return raw
      .split('|')
      .map((segment) =>
        segment
          .trim()
          .replace(/[_-]+/g, ' ')
          .replace(/\s+/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (match) => match.toUpperCase()),
      )
      .filter(Boolean)
      .join(' • ');
  };

  const activeRange = activeRowIndex !== null ? salaryRanges[activeRowIndex] ?? null : null;
  const activeAnchor = activeRowIndex !== null ? rowRefs.current[activeRowIndex] ?? null : null;
  const jobRange = salaryRanges.find((range) => range.type === 'job') ?? null;
  const marketReferenceRange = [...salaryRanges].reverse().find((range) => range.type !== 'job') ?? null;
  const salaryAnalysis = buildSalaryContextAnalysis(
    jobRange ? { min: jobRange.stats.min, max: jobRange.stats.max } : null,
    marketReferenceRange
      ? {
          min: marketReferenceRange.stats.min,
          max: marketReferenceRange.stats.max,
          sampleSize: marketReferenceRange.stats.sampleSize,
        }
      : null,
  );
  const salaryNarrative = marketReferenceRange
    ? buildSalaryAnalysisNarrative({
        locale: intl.locale,
        analysis: salaryAnalysis,
        benchmarkLabel: marketReferenceRange.label,
        jobRangeLabel: jobRange?.label ?? null,
        formatCurrency,
        marketMin: marketReferenceRange.stats.min,
        marketMax: marketReferenceRange.stats.max,
        jobMin: jobRange?.stats.min ?? null,
        jobMax: jobRange?.stats.max ?? null,
      })
    : '';
  const salaryComparisonSeoRows = buildSalaryComparisonSeoRows(
    salaryRanges.map((range) => ({
      label: range.label,
      min: range.stats.min,
      max: range.stats.max,
      median: getMedianMid(range.stats),
      type: range.type,
    })),
  );

  const detailChip = (key: string, label: React.ReactNode, value: string) => (
    <div
      key={key}
      className="rounded-md border border-[#DDD5C8] bg-white/80 px-2 py-1.5 dark:border-[#4A3520] dark:bg-[#170F07]/65"
    >
      <div className="text-[9px] uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C] leading-tight">{label}</div>
      <div className="mt-0.5 text-[11px] font-semibold text-[#1E1A14] dark:text-[#F2EADF] tabular-nums break-words leading-tight">{value}</div>
    </div>
  );

  return (
    <div className={`salary-distribution-chart ${className} mt-3 min-w-0`}>
      <div className="mb-2">
        <div className="text-sm font-semibold text-jc-ink">
          <FormattedMessage 
            id="salaryChart.salaryContext" 
            defaultMessage="Salary Context: {title}"
            description="Chart context label explaining what job title the salary data is based on, where {title} is the job title" 
            values={{ title: jobTitle || intl.formatMessage({ id: 'salaryChart.thisRole', defaultMessage: 'this role', description: 'Fallback text when job title is not available, used in salary chart labels' }) }}
          />
        </div>
      </div>

      <div className="bg-jc-surface p-3 rounded-xl border border-jc-border overflow-hidden">
        <p className="text-[10px] text-jc-muted px-1 sm:px-2 mb-2 leading-snug flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-jc-accent/70" aria-hidden />
          <span>
            <FormattedMessage
              id="salaryChart.segmentHint"
              defaultMessage="Hover or tap a row for full statistics (EUR / month on this chart)."
              description="Hint that salary range rows are interactive"
            />
          </span>
        </p>
        <div className="space-y-3 px-1 sm:px-2">
          {salaryRanges.map((salaryRange, index) => {
            const medianMid = getMedianMid(salaryRange.stats);
            const min = salaryRange.stats.min;
            const max = salaryRange.stats.max;
            const span = chartMax - chartMin;
            const x1 = ((min - chartMin) / span) * 100;
            const x2 = ((max - chartMin) / span) * 100;
            const xm = ((medianMid - chartMin) / span) * 100;
            const strokeAccent = salaryRange.color;
            const isActive = activeRowIndex === index;

            return (
              <div
                key={index}
                ref={(el) => {
                  rowRefs.current[index] = el;
                }}
                className={`rounded-xl border border-l-[3px] pl-2 pr-1.5 py-2 transition-colors cursor-pointer outline-none border-l-transparent hover:border-l-jc-accent/55 ${JD_MORE_INFO_CHART_HIT} focus-visible:ring-2 focus-visible:ring-jc-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jc-surface ${
                  isActive ? 'border-jc-accent/50 border-l-jc-accent/70 bg-jc-accent-muted/25' : 'border-transparent hover:border-jc-border/90 hover:bg-jc-elevated/40'
                }`}
                role="button"
                tabIndex={0}
                aria-expanded={isActive}
                onMouseEnter={() => onRowPointerEnter(index)}
                onMouseLeave={onRowPointerLeave}
                onClick={() => toggleRowClick(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleRowClick(index);
                  }
                }}
              >
                <div className="text-xs font-semibold text-jc-ink-secondary mb-1.5">
                  {salaryRange.label}
                </div>

                {/* Values at both ends + condensed range line between them */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-jc-muted tabular-nums shrink-0 min-w-[60px] text-left">
                    {formatCurrency(min)}
                  </span>
                  <div
                    className="relative h-6 flex-1 touch-manipulation"
                    role="img"
                    aria-label={intl.formatMessage(
                      {
                        id: 'salaryChart.rangeAria',
                        defaultMessage: 'Salary range from {min} to {max} per month, median near {median}',
                        description: 'Accessible label for salary range line graphic',
                      },
                      {
                        min: formatCurrency(min),
                        max: formatCurrency(max),
                        median: formatCurrency(medianMid),
                      },
                    )}
                  >
                    <div className="absolute left-0 right-0 top-1/2 h-px bg-jc-border/70 -translate-y-1/2 rounded-full" />
                    <div
                      className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full pointer-events-none"
                      style={{
                        left: `${x1}%`,
                        width: `${Math.max(x2 - x1, 0.5)}%`,
                        backgroundColor: strokeAccent,
                      }}
                    />
                    {/* Min endpoint */}
                    <div
                      className="absolute top-1/2 w-3 h-3 rounded-full border-2 border-jc-surface -translate-y-1/2 -translate-x-1/2 shadow-sm z-[1]"
                      style={{ left: `${x1}%`, backgroundColor: strokeAccent }}
                      aria-hidden
                    />
                    {/* Max endpoint */}
                    <div
                      className="absolute top-1/2 w-3 h-3 rounded-full border-2 border-jc-surface -translate-y-1/2 -translate-x-1/2 shadow-sm z-[1]"
                      style={{ left: `${x2}%`, backgroundColor: strokeAccent }}
                      aria-hidden
                    />
                    {/* Median marker */}
                    <div
                      className="absolute top-1/2 w-3.5 h-3.5 rounded-full border-2 border-jc-surface -translate-y-1/2 -translate-x-1/2 shadow-sm ring-1 ring-black/10 dark:ring-white/15 z-[2]"
                      style={{ left: `${xm}%`, backgroundColor: strokeAccent }}
                      aria-hidden
                    />
                  </div>
                  <span className="text-[10px] font-semibold text-jc-muted tabular-nums shrink-0 min-w-[60px] text-right">
                    {formatCurrency(max)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {marketReferenceRange ? (
          <div className="mt-3 px-1 sm:px-2 pt-2 border-t border-jc-border/80">
            <p className="text-[11px] font-semibold text-jc-ink-secondary mb-1">
              <FormattedMessage
                id="salaryChart.analysisTitle"
                defaultMessage="Salary analysis"
                description="Title for textual salary interpretation based on chart data"
              />
            </p>
            <p className="text-[11px] text-jc-muted leading-relaxed">{salaryNarrative}</p>
          </div>
        ) : null}
      </div>

      {salaryComparisonSeoRows.length > 0 ? (
        <div className="sr-only">
          <table>
            <caption>
              {intl.formatMessage(
                {
                  id: 'salaryChart.seoTable.caption',
                  defaultMessage: 'Monthly salary comparison for {title}',
                  description: 'Hidden salary table caption for search crawlers and accessibility',
                },
                {
                  title:
                    jobTitle ||
                    intl.formatMessage({
                      id: 'salaryChart.thisRole',
                      defaultMessage: 'this role',
                      description:
                        'Fallback text when job title is not available, used in salary chart labels',
                    }),
                },
              )}
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  <FormattedMessage
                    id="salaryChart.seoTable.market"
                    defaultMessage="Market"
                    description="Hidden salary table header"
                  />
                </th>
                <th scope="col">
                  <FormattedMessage
                    id="salaryChart.seoTable.lowerBound"
                    defaultMessage="Lower bound (25th percentile)"
                    description="Hidden salary table header"
                  />
                </th>
                <th scope="col">
                  <FormattedMessage
                    id="salaryChart.seoTable.median"
                    defaultMessage="Median"
                    description="Hidden salary table header"
                  />
                </th>
                <th scope="col">
                  <FormattedMessage
                    id="salaryChart.seoTable.upperBound"
                    defaultMessage="Upper bound (75th percentile)"
                    description="Hidden salary table header"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {salaryComparisonSeoRows.map((row) => (
                <tr key={`${row.market}-${row.lowerBound}-${row.upperBound}`}>
                  <td>{row.market}</td>
                  <td>
                    {formatCurrency(row.lowerBound)}/{formatPeriod('monthly')}
                  </td>
                  <td>
                    {formatCurrency(row.median)}/{formatPeriod('monthly')}
                  </td>
                  <td>
                    {formatCurrency(row.upperBound)}/{formatPeriod('monthly')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {activeRange && activeAnchor ? (
        <ChartPopoverPortal
          open
          anchorEl={activeAnchor}
          onClose={closePopover}
          showBackdrop={!prefersFineHover}
          width={360}
          title={activeRange.label}
          onPopoverMouseEnter={prefersFineHover ? clearHoverCloseTimer : undefined}
          onPopoverMouseLeave={prefersFineHover ? scheduleCloseHover : undefined}
        >
          <>
            <div className="rounded-md border border-[#DDD5C8] bg-[#F2EADF]/70 px-2.5 py-2 dark:border-[#4A3520] dark:bg-[#2A1D0F]/70">
              <div className="text-[9px] uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C]">
                <FormattedMessage id="salaryChart.popover.median" defaultMessage="Median band" description="Salary popover label" />
              </div>
              <div className="mt-0.5 text-[13px] font-bold text-[#1E1A14] dark:text-[#F2EADF] tabular-nums leading-tight">
                {formatSpread(activeRange.stats.median?.min ?? 0, activeRange.stats.median?.max ?? 0)}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {detailChip(
                'min',
                <FormattedMessage id="salaryChart.popover.min" defaultMessage="Minimum" description="Salary popover label" />,
                formatCurrency(activeRange.stats.min),
              )}
              {detailChip(
                'max',
                <FormattedMessage id="salaryChart.popover.max" defaultMessage="Maximum" description="Salary popover label" />,
                formatCurrency(activeRange.stats.max),
              )}
              {detailChip(
                'p25',
                <FormattedMessage id="salaryChart.popover.p25" defaultMessage="25th percentile" description="Salary popover label" />,
                formatSpread(activeRange.stats.percentile25?.min ?? 0, activeRange.stats.percentile25?.max ?? 0),
              )}
              {detailChip(
                'p75',
                <FormattedMessage id="salaryChart.popover.p75" defaultMessage="75th percentile" description="Salary popover label" />,
                formatSpread(activeRange.stats.percentile75?.min ?? 0, activeRange.stats.percentile75?.max ?? 0),
              )}
              {detailChip(
                'avg',
                <FormattedMessage id="salaryChart.popover.average" defaultMessage="Average band" description="Salary popover label" />,
                formatSpread(activeRange.stats.average?.min ?? 0, activeRange.stats.average?.max ?? 0),
              )}
              {detailChip(
                'sample',
                <FormattedMessage id="salaryChart.popover.sample" defaultMessage="Sample size" description="Salary popover label" />,
                (() => {
                  if (activeRange.type !== 'job') return String(activeRange.stats.sampleSize);
                  if (activeRange.jobRowKind === 'benchmark_estimate') {
                    return intl.formatMessage({
                      id: 'salaryChart.popover.benchmarkEstimateNote',
                      defaultMessage: 'Not from employer — modelled from benchmarks',
                      description: 'Popover note for estimated job row when ad has no salary',
                    });
                  }
                  if (activeRange.jobRowKind === 'extracted_no_quote') {
                    return intl.formatMessage({
                      id: 'salaryChart.popover.extractedNoQuoteNote',
                      defaultMessage: 'Numeric pay without quoted text in ad',
                      description: 'Popover note when salaryRange exists but salaryDetails is empty',
                    });
                  }
                  if (activeRange.jobRowKind === 'employer_quote' || activeRange.stats.sampleSize <= 1) {
                    return intl.formatMessage({
                      id: 'salaryChart.popover.fromAdText',
                      defaultMessage: 'Grounded in job-ad salary wording',
                      description: 'Popover note when pay row is backed by salaryDetails from listing',
                    });
                  }
                  return String(activeRange.stats.sampleSize);
                })(),
              )}
            </div>
            {activeRange.stats.percentile90 ? (
              <div className="mt-1.5">
                {detailChip(
                  'p90',
                  <FormattedMessage id="salaryChart.popover.p90" defaultMessage="90th percentile" description="Salary popover label" />,
                  formatSpread(activeRange.stats.percentile90.min, activeRange.stats.percentile90.max),
                )}
              </div>
            ) : null}
            {activeRange.stats.dimensionValue ? (
              <div className="rounded-md border border-[#DDD5C8] bg-white/70 px-2 py-1.5 dark:border-[#4A3520] dark:bg-[#170F07]/65">
                <div className="text-[9px] uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C]">
                  <FormattedMessage id="salaryChart.popover.dimension" defaultMessage="Dimension" description="Salary popover label" />
                </div>
                <div className="mt-0.5 text-[11px] font-medium text-[#1E1A14] dark:text-[#F2EADF] break-words leading-tight">
                  {formatDimensionValue(activeRange.stats.dimensionValue)}
                </div>
              </div>
            ) : null}
            <p className="text-[9px] text-jc-muted pt-1 border-t border-jc-border/80 mt-1.5 leading-tight">
              <FormattedMessage
                id="salaryChart.popover.monthlyNote"
                defaultMessage="All amounts are shown as EUR per month for comparison on this chart."
                description="Footnote in salary popover"
              />
            </p>
          </>
        </ChartPopoverPortal>
      ) : null}
    </div>
  );
});

SalaryDistributionChart.displayName = 'SalaryDistributionChart';
